import { Link, usePage } from '@inertiajs/react'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import type { SharedProps } from '@/frontend/types/inertia'
import {
  WORKER_NAV_GROUPS,
  visibleGroupPages,
  type WorkerGroupPage
} from './workerGroups'

interface Props {
  // Agent that scopes the pages (worker show sections or the canonical
  // agent-scoped org pages under /dashboard/digital_workers/:id/...).
  workerId: string
  // Authoritative section key on the worker Show page; org-wide filtered
  // pages omit it and the URL decides which page is current.
  current?: string
  // Instance readiness for gated pages; derived from navigableResources
  // when the host page does not know it (org-wide filtered pages).
  workerReady?: boolean
}

/**
 * In-content navigation of the agent portal. The taxonomy groups live in the
 * expanded agent row in the global sidebar; this component renders only the
 * deeper sisters of the active group — hoje só Perfil e Conexões têm irmãs.
 * Single-page groups return null so Chat/Processos/Rotinas/Memória do not
 * leave an empty navigation row below the FichaHeader.
 *
 * Locks become states (SZ-1583): gated groups/sisters stay real links while
 * the instance is not ready — dimmed, with a status dot (pulsing while
 * provisioning) — and the section page explains why (SectionUnavailableState).
 */
export default function WorkerPortalNav({ workerId, current, workerReady }: Props) {
  const { props: pageProps, url } = usePage<SharedProps>()
  const workerFirstNavEnabled = pageProps.featureFlags?.worker_first_nav_enabled ?? false
  const businessProcessesEnabled = pageProps.featureFlags?.business_processes_enabled ?? false
  if (!workerFirstNavEnabled) return null

  const [path, query] = url.split('?')
  const params = new URLSearchParams(query ?? '')
  const isCurrent = (page: WorkerGroupPage) =>
    current ? page.key === current : page.isActive(path, workerId, params)

  const navigableWorker = (pageProps.navigableResources?.workers ?? [])
    .find((candidate) => candidate.id === workerId)
  const resourceStatus = navigableWorker?.provisioning_status
  const ready = workerReady ?? resourceStatus === 'ready'
  const provisioning = resourceStatus === 'pending' || resourceStatus === 'provisioning'
  // The tab bar this nav replaces carried ?onboarding=1 across sections so
  // the post-wizard panel survives in-page navigation; keep that for section
  // links (filtered global pages don't consume it).
  const onboardingSuffix = params.get('onboarding') === '1' ? '?onboarding=1' : ''
  const withOnboarding = (href: string) => (href.includes('?') ? href : `${href}${onboardingSuffix}`)

  const activeGroup = WORKER_NAV_GROUPS.find((group) => group.pages.some(isCurrent)) ?? null
  const sisters = activeGroup ? visibleGroupPages(activeGroup, businessProcessesEnabled) : []

  if (!activeGroup || sisters.length < 2) return null

  const gatedDot = (
    <>
      <span
        className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
          provisioning ? 'bg-warning animate-live-pulse' : 'bg-faint-foreground'
        }`}
        aria-hidden="true"
      />
      <span className="sr-only">
        {provisioning ? '(instância em preparação)' : '(instância indisponível)'}
      </span>
    </>
  )

  return (
    <nav
      data-testid="wfn-group-subnav"
      aria-label={`Seções de ${activeGroup.label}`}
      className="flex max-w-full gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={agentHueStyle(workerId)}
    >
      {sisters.map((page) => {
        const activePage = isCurrent(page)
        // Same instance gating as the sidebar groups (SZ-1583): the gated
        // sister stays navigable and explains its unavailable state in-page.
        const gatedOff = page.gate === 'instance' && !ready

        return (
          <Link
            key={page.key}
            href={withOnboarding(page.href(workerId))}
            aria-current={activePage ? 'page' : undefined}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[13px] font-medium transition-colors duration-150 ${
              activePage
                ? 'bg-agent-soft text-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            } ${gatedOff ? 'opacity-60' : ''}`}
          >
            {page.label}
            {gatedOff && gatedDot}
          </Link>
        )
      })}
    </nav>
  )
}
